import { tagChipValues } from "src/pages/ad-studio/tagging/tag-values";

export type CreativeTagConfidence = "low" | "medium" | "high";

export type CreativeTagView = {
  creativeId: string;
  creativeType: string | null;
  dimensionKey: string;
  dimensionName: string;
  values: string[];
  version: number | null;
  confidence: CreativeTagConfidence | null;
  reason: string | null;
  /** None of the dimension's values fit, so the tag has no value. */
  noMatch: boolean;
  /** Values the tag can be switched to in place; null when not editable. */
  allowedValues: string[] | null;
};

/**
 * A tag can be edited in place when its dimension publishes a value list and
 * the creative currently holds exactly one value to replace.
 */
export function isEditableCreativeTag(
  tag: Pick<CreativeTagView, "allowedValues" | "values">,
): tag is Pick<CreativeTagView, "values"> & { allowedValues: string[] } {
  return (
    tag.allowedValues != null &&
    tag.allowedValues.length > 0 &&
    tag.values.length === 1
  );
}

export type CreativeTagGroup = {
  creativeId: string;
  creativeType: string | null;
  tags: CreativeTagView[];
};

export function parseCreativeTagConfidence(
  value: string | null | undefined,
): CreativeTagConfidence | null {
  if (value === "low" || value === "medium" || value === "high") {
    return value;
  }
  return null;
}

export function confidenceLabel(confidence: CreativeTagConfidence): string {
  if (confidence === "high") return "High";
  if (confidence === "medium") return "Medium";
  return "Low";
}

/**
 * How the tag came to be: whether no value fit, the tagger's confidence,
 * then its reason. Null when the tag carries none of them.
 */
export function creativeTagReason(
  tag: Pick<CreativeTagView, "confidence" | "reason" | "noMatch">,
): string | null {
  const parts: string[] = [];
  if (tag.noMatch) {
    parts.push("No value fits.");
  }
  if (tag.confidence) {
    parts.push(`${confidenceLabel(tag.confidence)} confidence.`);
  }
  const reason = tag.reason?.trim();
  if (reason) {
    parts.push(reason);
  }
  return parts.length > 0 ? parts.join(" ") : null;
}

export type CreativeTagValueChange = {
  creativeId: string;
  dimensionKey: string;
  value: string;
};

/**
 * The tag rows as they will read once a manual edit lands: the matching row
 * takes the new value and drops the model's confidence and reason, since the
 * server replaces both. Rows are the raw query shape so the result can be
 * written straight back into the query cache.
 */
export function applyCreativeTagValue<
  Row extends {
    creativeId: string;
    dimensionKey: string;
    value: unknown;
    confidence?: string | null;
    reason?: string | null;
    noMatch?: boolean;
  },
>(rows: ReadonlyArray<Row>, change: CreativeTagValueChange): Row[] {
  return rows.map((row) =>
    row.creativeId === change.creativeId &&
    row.dimensionKey === change.dimensionKey
      ? {
          ...row,
          value: change.value,
          confidence: null,
          reason: null,
          noMatch: false,
        }
      : row,
  );
}

export function groupCreativeTags(
  rows: ReadonlyArray<{
    creativeId: string;
    creativeType?: string | null;
    dimensionKey: string;
    dimensionName: string;
    value: unknown;
    version?: number | null;
    confidence?: string | null;
    reason?: string | null;
    noMatch?: boolean;
    allowedValues?: string[] | null;
  }>,
): CreativeTagGroup[] {
  const groups = new Map<string, CreativeTagGroup>();
  for (const row of rows) {
    let group = groups.get(row.creativeId);
    if (!group) {
      group = {
        creativeId: row.creativeId,
        creativeType: row.creativeType ?? null,
        tags: [],
      };
      groups.set(row.creativeId, group);
    }
    group.tags.push({
      creativeId: row.creativeId,
      creativeType: row.creativeType ?? null,
      dimensionKey: row.dimensionKey,
      dimensionName: row.dimensionName,
      values: tagChipValues(row.value),
      version: row.version ?? null,
      confidence: parseCreativeTagConfidence(row.confidence),
      reason: row.reason ?? null,
      noMatch: row.noMatch ?? false,
      allowedValues: row.allowedValues ?? null,
    });
  }
  return [...groups.values()];
}
